// The shared form error list: a `form__error-list` ul with one `form__error`
// li per message. Rendered identically for non-field errors, per-field errors,
// and the expired-token form errors, so it lives here as one leaf component to
// keep that markup byte-identical everywhere.
import { Immutable } from "immer";
import React from "react";

import {
    FORM_ERROR_CLASS_NAME,
    FORM_ERROR_LIST_CLASS_NAME,
} from "../classNames";

// Structural view of the Python `FormErrorMessage` NamedTuple
// (tsicommon.cms.react_forms). `is_html` marks a SafeString message, which the
// Django templates rendered unescaped (#35390).
export type FormErrorMessage = Immutable<{
    text: string;
    is_html: boolean;
}>;

type IProps = Immutable<{
    /** Plain strings (e.g. REST API errors) always render as text. */
    errors: readonly (string | FormErrorMessage)[];
    /** Lets a field's control point at its errors via `aria-describedby`. */
    id?: string;
}>;

// The data-testid attrs are the stable hooks for the Django SSR tests, which
// must not select by style classnames.
export const FormErrorList = (props: IProps): React.JSX.Element => (
    <ul
        id={props.id}
        className={FORM_ERROR_LIST_CLASS_NAME}
        data-testid="form-error-list"
    >
        {props.errors.map((error, i) => {
            const { text, is_html } =
                typeof error === "string"
                    ? { text: error, is_html: false }
                    : error;
            return is_html ? (
                <li
                    className={FORM_ERROR_CLASS_NAME}
                    data-testid="form-error"
                    key={i}
                    dangerouslySetInnerHTML={{ __html: text }}
                />
            ) : (
                <li
                    className={FORM_ERROR_CLASS_NAME}
                    data-testid="form-error"
                    key={i}
                >
                    {text}
                </li>
            );
        })}
    </ul>
);
